/**
 * useTinyMce – A custom React Hook for TinyMCE editor integration (viewer & editor)
 *
 * This hook provides:
 * 1. <TinyMce /> component – A configurable TinyMCE editor
 * 2. <Renderer /> component – Safely displays content seamless from the TinyMce.
 * 3. output – A string representing the current editor content (HTML).
 * 4. reset - Reset to initial value of editor & viewer.
 *
 *  Props:
 * - initialValue: string – The default HTML content to load into the editor
 * - TinyMce:
 *    - classnames?: string – Optional editor classnames
 *
 * Usage:
 * const { TinyMce, Renderer, output, reset} = useTinyMce("<p>Hello</p>");
 *
 * <TinyMce classnames={'sample'} />
 * <Renderer />
 */

import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import TinyMCE from "react-tinymce";
import { useSelector } from "react-redux";
import { DEFAULT_FONTS } from "src/constants/fonts";
import cx from 'classnames';
import { guid } from "src/utils/util";
import styles from './styles.module.scss';

import _ from "lodash";

// Types
type UseTinyMceReturn = (initialValue: string) => {
  Renderer: FC;
  TinyMce: FC<{ classnames?: string, toolbar?: string, plugins?: string, quickToolbar?: boolean, toolbarMode?: string, transparentBackground?: boolean }>;
  output: string;
  reset: () => void;
};
type RendererFn = (value: string) => JSX.Element;
type TinyMceFn = (
  value: string,
  handleChange: (data: string) => void,
  classnames?: string,
  tinyMceRef?: any,
  toolbar?: string,
  plugins?: string,
  quickToolbar?: boolean,
  toolbarMode?: string,
  transparentBackground?: boolean
) => JSX.Element;

export const useTinyMce: UseTinyMceReturn = (initialValue) => {
  const [value, setValue] = useState<string>(initialValue ?? '');
  const tinyMceRef = useRef<any>(null);
  const valueRef = useRef(initialValue ?? '');

  useEffect(() => { // sync with initialValue (parent)
    valueRef.current = initialValue ?? '';
    setValue(initialValue ?? '');
    if (tinyMceRef.current) {
      tinyMceRef.current.setContent(initialValue ?? '');
    }
  }, [initialValue]);

  const handleChange = useCallback((val: string) => {
    setValue(val);
  }, []);

  const rendererWrapper = useMemo(() => {
    return function RenderComponent() {
      return Renderer(value);
    };
  }, [value]);

  const tinyMceWrapper = useMemo(() => {
    return function TinyMceComponent({ classnames, toolbar, plugins, quickToolbar, toolbarMode, transparentBackground }: { classnames?: string, toolbar?: string, plugins?: string, quickToolbar?: boolean, toolbarMode?: string, transparentBackground?: boolean }) {
      return TinyMce(valueRef.current, handleChange, classnames, tinyMceRef, toolbar, plugins, quickToolbar, toolbarMode, transparentBackground);
    };
  }, [handleChange]);

  const handleReset = (): void => {
    valueRef.current = initialValue ?? '';
    setValue(initialValue ?? '');
    if(tinyMceRef.current) {
      tinyMceRef.current.setContent(initialValue ?? '');
    }
  }

  return { Renderer: rendererWrapper, TinyMce: tinyMceWrapper, output: value, reset: handleReset };
};

const Renderer: RendererFn = (value) => {
  return <div dangerouslySetInnerHTML={{ __html: value }}></div>;
};

const TinyMce: TinyMceFn = (value, handleChange, classnames, tinyMceRef, toolbar, plugins, quickToolbar, toolbarMode, transparentBackground = false ) => {
  const preData = useSelector((state: any) => _.get(state, "accounts.item")); // Get the general config's
  const key = useMemo(() => guid(), []);
  
  // config
  const toolbarValues = toolbar || 'bold italic | alignleft aligncenter alignright | numlist bullist | fontselect | fontsizeselect | forecolor backcolor | image link | code';
  const pluginValues = plugins || "noneditable, variable, widget,  autolink, link, image, code, textcolor, lists, paste";

  /* Methods */
  // getFonts (config)
  const getFonts = (): string => {
    var fontArray = [...DEFAULT_FONTS]; // default fonts
    var customFonts = _.get(preData, "theme.fonts", []); // custom fonts (i.e, available in store data)

    if (customFonts && customFonts.length > 0) {
      fontArray = [...customFonts, ...fontArray];
    }

    return fontArray
      .map((font) => {
        return `${font.name}=${font.fontFamily}`;
      })
      .join(";");
  };

  // handleChange
  const handleTinyMceChanges = (data: string): void => {
    handleChange(data);
  };

  return (
    <div className={cx(styles.editor,classnames)}>
      <TinyMCE
        key={key}
        content={value}
        config={{
          branding: false,
          statusbar: false,
          plugins: `${pluginValues} ${quickToolbar ? ', quickbars' : ''}`,
          menubar: false,
          toolbar: quickToolbar ? false : toolbarValues,
          resize: false,
          toolbar_mode: toolbarMode || "wrap",
          quickbars_insert_toolbar: false,
          quickbars_selection_toolbar: quickToolbar ? toolbarValues : false,
          noneditable_noneditable_class: "mceNonEditable",
          content_css: [
            "https://fonts.googleapis.com/css?family=Montserrat:100,100i,200,200i,300,300i,400,400i,500,500i,600,600i,700,700i,800,800i,900,900i",
            "https://maxcdn.bootstrapcdn.com/bootstrap/latest/css/bootstrap.min.css",
            _.get(preData, "theme.customStyleSheet", ""),
          ],
          font_formats: getFonts(),
          content_style: `body { 
            font-family: 'Montserrat', sans-serif; 
            ${transparentBackground ? "background: transparent !important;" : ""}
            }
            .mce-content-body p {
              margin: 0px;
            }`,
          default_link_target: "_blank",
          fontsize_formats:
            "5pt 5.5pt 6.5pt 7.5pt 8pt 9pt 10pt 10.5pt 11pt 12pt 14pt 16pt 18pt 20pt 22pt 24pt 26pt 28pt 36pt 48pt 72pt",
          paste_retain_style_properties: "color",
          browser_spellcheck: true,
          convert_urls: false,
          relative_urls: false,
          remove_script_host: false,
          setup: (editor: any) => {
          if(tinyMceRef){
              tinyMceRef.current = editor;
            }
            // Custom Events,
          editor.on("input", (): void => { // Fires on every keystroke (or) input (typing, paste, delete)
              handleTinyMceChanges(editor.getContent());
            });
          editor.on("NodeChange", (): void => { // Fires on DOM changes that is dom structure changes (i.e., Ctrl+A + Delete, undo/redo, )
              handleTinyMceChanges(editor.getContent());
            });
          },
        }}
      />
    </div>
  );
};
